import React, { useEffect, useState } from "react";
import BaseCard from "../../../../components/ui/BaseCard";
import FileChip from "../../../../components/ui/FileChip";
import { formatPhoneNumber } from "../../../../utils/helpers";

const ANNUAL_REVENUE = {
  1: "Under $1 Million",
  "5-10": "$5 - $10 Million",
  11: "Above $10 Million",
};

const CompanyDetailsView = ({ values, onEdit, isRegistrationLocked = false }) => {
  const [logoFailed, setLogoFailed] = useState(false);
  // A newly saved logo gets a fresh chance to load.
  useEffect(() => setLogoFailed(false), [values?.logo?.url]);

  const text = (value) => <p className="text-sm">{value || "-"}</p>;

  const attachment = (file) => (file?.fileName || file?.name ? <FileChip file={file} /> : text());

  const rows = (items, twoColumns = false) => (
    <div className={twoColumns ? "grid grid-cols-1 lg:grid-cols-2 lg:gap-x-10" : ""}>
      {items.map(([label, value], i) => {
        const isLast = i === items.length - 1;
        const isOnLastPair = twoColumns && i >= items.length - (items.length % 2 === 0 ? 2 : 1);
        return (
          <div
            key={label}
            className={`flex flex-col md:flex-row md:items-center gap-1 pb-2 pt-4 ${
              isLast ? "" : "border-b"
            } ${isOnLastPair ? "lg:border-b-0" : ""}`}
          >
            <p className="font-normal text-sm md:w-60 shrink-0">{label}</p>
            <div className="flex-1 min-w-0">{value}</div>
          </div>
        );
      })}
    </div>
  );

  const contactSupport = (
    <p className="text-sm">
      <span
        className="text-primary cursor-pointer hover:underline"
        onClick={() =>
          window.open(
            "mailto:helpdesk@techdomain.com.au?subject=Update Registration Details&body=Hi, I need to update my company's registration details. Please help.",
          )
        }
      >
        Contact Support
      </span>{" "}
      to update details
    </p>
  );

  const card = (title, section, children, locked = false) => (
    <BaseCard className="py-2 px-4 sm:px-6 rounded-lg" elevation={1}>
      <div className="bg-light px-4 py-2 mt-2 mb-2 rounded-md flex justify-between items-center">
        <h6 className="text-dark font-normal">{title}</h6>
        {locked
          ? contactSupport
          : onEdit && (
              <i
                className="fa-light fa-pencil text-gray-900 cursor-pointer"
                onClick={() => onEdit(section)}
              ></i>
            )}
      </div>
      <div className="px-4">{children}</div>
    </BaseCard>
  );

  const logoUrl = values?.logo?.url;

  return (
    <>
      {card(
        "Company Logo",
        "logo",
        <div className="py-4">
          <div className="h-40 flex flex-col items-center justify-center gap-2 w-full border-2 border-dashed border-gray-300 rounded-md p-4">
            {logoUrl && !logoFailed ? (
              <img
                src={logoUrl}
                alt="Company Logo"
                className="max-h-32 object-contain rounded"
                onError={() => setLogoFailed(true)}
              />
            ) : (
              <>
                <i className="fa-regular fa-image text-gray-400 text-2xl"></i>
                <span className="text-sm text-gray-500">No logo uploaded</span>
              </>
            )}
          </div>
        </div>,
      )}

      {card(
        "Company Information",
        "information",
        rows(
          [
            ["Name", text(values?.name)],
            ["Email", text(values?.email)],
            ["Website", text(values?.website)],
            ["Phone Number", text(formatPhoneNumber(values?.mobileNumber))],
            ["Fax", text(values?.fax)],
            ["Annual Revenue", text(ANNUAL_REVENUE[values?.companyAnnualRevenue])],
            // The full address line only; city, state and the rest are edited in the modal.
            ["Address", text(values?.address)],
          ],
          true,
        ),
      )}

      {card(
        "Registration",
        "registration",
        rows([
          ["Registration Number", text(values?.registrationNumber)],
          ["VAT/GST Registration", text(values?.vatNumber)],
          ["Registration Certificate", attachment(values?.registrationCertificate)],
        ]),
        isRegistrationLocked,
      )}

      {card(
        "Insurance",
        "insurance",
        rows([
          ["Public Liability / Public Indemnity", attachment(values?.publicLiability)],
          ["Worker Compensation", attachment(values?.workerCompensation)],
        ]),
      )}
    </>
  );
};

export default CompanyDetailsView;
